import type { User } from 'src/types/user';

import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useMutation } from '@tanstack/react-query';

import { useAuth } from 'src/AuthContext';
import { takeoffUrl, designToolUrl } from 'src/globalSettings';

import { setActiveVersionV2 } from '../../services/projectsV2';

import { timeAgo } from 'src/utils/timeAgo';
import type { ProjectV2 } from 'src/types/projectV2';
import ProjectActions from './ProjectActions';

type Props = {
  view: 'all' | 'user';
  statusFilter: 'All' | 'Quoted' | 'Draft';
  users: User[];
  projects: ProjectV2[];
  selectedOwner: string | undefined;
  cursor?: string | null;
};

const handleEdit = (project: ProjectV2, isRetail: boolean) => {
  if (project.projectType?.toString().toLowerCase() === 'blueprint' && !project.takeoffSubmitted) {
    window.location.href = `${takeoffUrl}/?project_id=${project.id}`;
  } else {
    let url = `${designToolUrl}/?project_id=${project.id}`;
    if (isRetail) url += '&retail=true';
    window.location.href = url;
  }
};

const ProjectList: React.FC<Props> = ({
  view,
  statusFilter,
  users,
  projects,
  selectedOwner = 'All',
  cursor,
}) => {
  const { user } = useAuth();
  const isRetail = useMemo(() => !user.isMultifamily, [user]);

  // Local copy for UI-only mutations and searching
  const [localGroups, setLocalGroups] = useState<ProjectV2[]>(projects);

  useEffect(() => {
    setLocalGroups(projects);
  }, [projects, view]);

  const filteredGroups = useMemo(() => localGroups, [localGroups]);

  const setActiveVersionMutation = useMutation({
    mutationFn: (vars: { parentProjectId: string; projectId: string }) =>
      setActiveVersionV2(vars.parentProjectId, vars.projectId),
    onSuccess: (newProjectData, vars) => {
      const newProject = newProjectData.data;
      setLocalGroups((prev) => {
        let found = false;
        const next = prev.map((p) => {
          if (p.parentProjectId !== vars.parentProjectId) return p;
          if (p.parentProjectId === newProject.parentProjectId) {
            found = true;
            return { ...newProject };
          }
          return { ...p };
        });
        return found ? next : [...next, { ...newProject }];
      });

      toast.success('Active version updated');
    },
    onError: () => toast.error('Failed to set active version'),
  });

  const handleSetActiveVersion = (parentProjectId?: string, newActiveProjectId?: string) => {
    if (!parentProjectId || !newActiveProjectId) return;
    setActiveVersionMutation.mutate({ parentProjectId, projectId: newActiveProjectId });
  };

  const handleDeleteParentProjectLocally = (parentProjectId: string) => {
    setLocalGroups((prev) => prev.filter((g) => g.parentProjectId !== parentProjectId));
  };

  // const handleDeleteVersion = (parentProjectId: string, deletedProjectId: string) => {
  //   setLocalGroups((prev) =>
  //     prev.flatMap((group) => {
  //       if (group.parentProjectId !== parentProjectId) return [group];

  //       const remaining = (group ?? []).filter((p) => p.id !== deletedProjectId);

  //       if (remaining.length === 0) return [];

  //       const nextActive =
  //         [...remaining].sort(
  //           (a, b) => new Date(b.updatedAt ?? 0).getTime() - new Date(a.updatedAt ?? 0).getTime(),
  //         )[0] ?? remaining[0];

  //       const normalized = remaining.map((p) => ({ ...p, active: p.id === nextActive.id }));

  //       return [
  //         {
  //           ...group,
  //           parentProject: {
  //             ...group.parentProject,
  //             activeProjectId: nextActive.id!,
  //             activeProjectName: nextActive.versionName || group.parentProject.activeProjectName,
  //           },
  //           projects: normalized,
  //         },
  //       ];
  //     }),
  //   );
  // };

  // const handleUpdateSalesforceId = (parentProjectId: string, sfId: string) => {
  //   setLocalGroups((prev) =>
  //     prev.map((group) => {
  //       if (group.parentProjectId === parentProjectId && group.projects) {
  //         return {
  //           ...group,
  //           projects: group.projects.map((p) => ({ ...p, salesforceId: sfId })),
  //         };
  //       }
  //       return group;
  //     }),
  //   );
  // };

  if (!localGroups) {
    return (
      <div className='text-center py-5'>
        <span>Loading...</span>
      </div>
    );
  }

  if (filteredGroups.length === 0 && cursor !== undefined) {
    return (
      <div className='text-center py-5'>
        <span>No projects found</span>
      </div>
    );
  }

  return (
    <div className='projects-grid'>
      {filteredGroups.map((displayProject) => {
        if (!displayProject) return null;
        return (
          <div
            key={displayProject.id}
            className='project-card'
            onClick={() => handleEdit(displayProject, isRetail)}
          >
            <div className='project-card-top-wrapper'>
              <img src='/project-preview.webp' width='100%' height='100%' alt='' />
              <p className='badge'>{displayProject.projectType}</p>
            </div>

            <div className='project-card-bottom-wrapper'>
              <p className='title'>{displayProject.name}</p>
              <p className='detail'>Version: {displayProject.versionName}</p>
              <div className='project-card-row'>
                <p className='detail'>{displayProject.unitNumbersQty} Units</p>
                <svg height='4' width='4' xmlns='http://www.w3.org/2000/svg'>
                  <circle r='2' cx='2' cy='2' fill='#9CA3AF' />
                </svg>
                <p className='detail'>Edited {timeAgo(displayProject.updatedAt!)}</p>
              </div>
              {view == 'all' && selectedOwner == 'All' && (
                <p
                  style={{
                    fontSize: '14px',
                  }}
                  className='detail'
                >
                  {displayProject.owner}
                </p>
              )}
              {/* {
                displayProject?.projectType?.toLowerCase() === 'blueprint' &&  statusInfo && statusInfo?.status !== 'NOT_FOUND'  && (
                    <Progress percent={statusInfo?.progress_percent} status={status} />
                )
              } */}
            </div>

            <div className='project-card-actions' onClick={(e) => e.stopPropagation()}>
              <ProjectActions
                project={displayProject}
                users={users}
                onSetActiveVersion={(newProjectId) =>
                  handleSetActiveVersion(displayProject.parentProjectId, newProjectId)
                }
                onDeleteParentProjectLocally={handleDeleteParentProjectLocally}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ProjectList;
